import { useCallback, useRef } from "react";
import { useDrag, useDrop } from "react-dnd";

import Icon from "../../Utils/Icon";
import Collapse from "../../Utils/Collapse";
import OptionDescriptionButton from "../OptionDescriptionButton";
import OptionDescription from "../OptionDescription";
import { useSortableListInputContext } from ".";

export const SORTABLE_DRAGGABLE_LIST_SOURCE = "SORTABLE_DRAGGABLE_LIST_SOURCE";

const SortableListDraggableSource = props => {
  const {
    option,
    moveSelectedItems,
    moveOptions,
    addToSelectedItems,
    addItemToOptions,
    showPosition
  } = props;
  const containerRef = useRef();

  const { options, selectedItems, maximumOptions } = useSortableListInputContext();

  // This callback is used to handle when another item is hovered on the current draggable
  // source. It allows managing the current items list reordering, and adding new items
  // to the list when they're dragged from another list over the current item.
  const handleHovered = useCallback(
    (draggedItem, monitor) => {
      if (!containerRef.current) return;

      // If the dragged item wasn't already in the current list, add it to the list and
      // assign it the last position in the list so it can be reordered immediately after
      // it's added.
      if (draggedItem.isSelected !== option.isSelected) {
        if (draggedItem.isSelected) {
          addItemToOptions(draggedItem);
          draggedItem.position = options.length;
        } else {
          // Check if the maximum options limit is reached
          // before adding a new item to the selected items list.
          if (!maximumOptions || selectedItems.length < maximumOptions) {
            addToSelectedItems(draggedItem);
            draggedItem.position = selectedItems.length;
          } else {
            return;
          }
        }

        draggedItem.isSelected = option.isSelected;
      }

      // Get the positions of the dragged item and the currently hovered item to compare
      // and reorder them.
      const sourcePosition = draggedItem.position;
      const targetPosition = option.position;

      // Don't reorder an item if it targets the same position, to avoid update loops
      // while the component is reordered.
      if (sourcePosition === targetPosition) return;

      // Get the current hovered item position on the screen, and the current dragged
      // element item position.
      const hoverBoundingRect = containerRef.current?.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      // If the dragged item position in the list is before the hovered item and its
      // position on the screen is above the center of the hovered item, don't do anything
      // as they're already in the right order.
      if (sourcePosition < targetPosition && hoverClientY < hoverMiddleY) {
        return;
      }

      // If the dragged item position in the list is after the hovered item and its
      // position on the screen is below the center of the hovered item, don't do anything
      // as they're already in the right order.
      if (sourcePosition > targetPosition && hoverClientY > hoverMiddleY) {
        return;
      }

      // If the item positions must change, reorder the items of the target list
      if (draggedItem.isSelected) {
        moveSelectedItems(draggedItem, sourcePosition, targetPosition);
      } else {
        moveOptions(draggedItem, sourcePosition, targetPosition);
      }

      // Force assigning the position to the monitored dragged item to avoid update loops
      draggedItem.position = targetPosition;
    },
    [
      addItemToOptions,
      addToSelectedItems,
      moveOptions,
      moveSelectedItems,
      option.isSelected,
      option.position,
      options.length,
      selectedItems.length,
      maximumOptions
    ]
  );

  const [{ handlerId, isDragging }, droppableRef] = useDrop(() => {
    return {
      accept: SORTABLE_DRAGGABLE_LIST_SOURCE,
      hover: handleHovered,
      collect: monitor => {
        const draggedItem = monitor.getItem();
        const isDragging = draggedItem?.id === option.id;
        const handlerId = monitor.getHandlerId();

        return { isDragging, handlerId };
      }
    };
  }, [option, handleHovered]);

  const [, draggableRef, previewRef] = useDrag(
    () => ({
      type: SORTABLE_DRAGGABLE_LIST_SOURCE,
      item: option
    }),
    [option]
  );

  const opacity = isDragging ? 0.2 : 1;

  // The whole item stays the drop target and the dragged preview, but only its handle
  // starts a drag, so that tapping the description button or reading the description
  // never moves the option, especially with the touch backend that starts dragging as
  // soon as the draggable element is touched.
  previewRef(droppableRef(containerRef));

  return (
    <Collapse>
      <div
        className="sortable-list-droppable-zone-item padding-sm margin-y-xxs"
        ref={containerRef}
        style={{ opacity }}
        data-handler-id={handlerId}
      >
        <div className="d-flex align-items-center">
          <span
            ref={draggableRef}
            className="sortable-list-droppable-zone-item-handle d-flex align-items-center flex-1"
          >
            <Icon className="fa fa-grip-vertical margin-right-xs" />
            <span className="flex-1">{option.name}</span>
          </span>

          {option.description && <OptionDescriptionButton />}

          {showPosition && (
            <span className="sortable-list-droppable-zone-item-position badge bg-primary margin-left-xs">
              {option.position + 1}
            </span>
          )}
        </div>

        {option.description && <OptionDescription description={option.description} />}
      </div>
    </Collapse>
  );
};

export default SortableListDraggableSource;
